import { Link } from 'react-router-dom';
import { Phone, Menu, X, Truck, MapPin, Clock, Mail, Star, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { SEO_BASE } from './SEO';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Blog', path: '/blog' },
  { label: 'FAQ', path: '/faq' },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-800" data-testid="site-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <Truck className="w-7 h-7 text-red-600" />
            <span className="text-white font-bold text-xl">A2B Towing</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {NAV_LINKS.map(link => (
              <Link key={link.path} to={link.path} className="text-gray-300 hover:text-white text-sm font-medium transition-colors" data-testid={`nav-${link.label.toLowerCase()}`}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/quote" data-testid="header-quote-btn">
              <button className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-md transition-all">
                Get Quote
              </button>
            </Link>
            <a href="tel:+19162343923" className="flex items-center gap-2 text-white font-semibold text-sm" data-testid="header-phone">
              <Phone className="w-4 h-4 text-red-600" />
              (916) 234-3923
            </a>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2" data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-zinc-900 border-t border-zinc-800" data-testid="mobile-nav">
          <div className="container mx-auto px-4 py-4 space-y-3">
            {NAV_LINKS.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)} className="block text-gray-300 hover:text-white text-base font-medium py-2" data-testid={`mobile-nav-${link.label.toLowerCase()}`}>
                {link.label}
              </Link>
            ))}
            <Link to="/quote" onClick={() => setMobileOpen(false)} className="block">
              <button className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-md mt-2">
                Get Quote
              </button>
            </Link>
            <a href="tel:+19162343923" className="flex items-center justify-center gap-2 text-white font-semibold py-3">
              <Phone className="w-4 h-4 text-red-600" />
              (916) 234-3923
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

const GBP_URL = "https://www.google.com/maps/place/A2B+Towing+%26+Roadside+Assistance";

function FooterContactInfo() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Truck className="w-6 h-6 text-red-600" />
        <span className="text-white font-bold text-lg">A2B Towing</span>
      </div>
      <p className="text-gray-400 text-sm leading-relaxed mb-4">Orangevale's most trusted towing company. Professional towing & roadside assistance available 24/7.</p>
      <div className="space-y-2">
        <div className="flex items-start gap-2 text-gray-400 text-sm"><MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /><span data-testid="footer-address">Orangevale, CA 95662</span></div>
        <div className="flex items-center gap-2 text-gray-400 text-sm"><Phone className="w-4 h-4 text-red-600 flex-shrink-0" /><a href="tel:+19162343923" className="hover:text-white transition-colors" data-testid="footer-phone">(916) 234-3923</a></div>
        <div className="flex items-center gap-2 text-gray-400 text-sm"><Mail className="w-4 h-4 text-red-600 flex-shrink-0" /><a href="mailto:A2broadside916@gmail.com" className="hover:text-white transition-colors" data-testid="footer-email">A2broadside916@gmail.com</a></div>
        <div className="flex items-center gap-2 text-gray-400 text-sm"><Clock className="w-4 h-4 text-red-600 flex-shrink-0" /><span data-testid="footer-hours">Open 24/7 - Emergency Service</span></div>
      </div>
      <a href={GBP_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 hover:border-red-600 rounded-lg px-4 py-3 transition-all group" data-testid="footer-google-review">
        <div className="flex items-center gap-1">{['fs1','fs2','fs3','fs4','fs5'].map((sid) => (<Star key={sid} className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />))}</div>
        <div className="text-xs"><span className="text-white font-semibold block">5.0 on Google</span><span className="text-gray-500 group-hover:text-red-600 transition-colors">Leave a Review</span></div>
        <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-red-600 transition-colors" />
      </a>
    </div>
  );
}

function FooterLinks() {
  return (
    <>
      <div>
        <h3 className="text-white font-semibold mb-4">Quick Links</h3>
        <ul className="space-y-2">
          {NAV_LINKS.map(link => (<li key={link.path}><Link to={link.path} className="text-gray-400 hover:text-white text-sm transition-colors">{link.label}</Link></li>))}
          <li><Link to="/quote" className="text-gray-400 hover:text-white text-sm transition-colors">Request Quote</Link></li>
        </ul>
      </div>
      <div>
        <h3 className="text-white font-semibold mb-4">Our Services</h3>
        <ul className="space-y-2 text-gray-400 text-sm">
          {['Towing Services', 'Roadside Assistance', 'Hauling Services', 'Load Transfer', 'Flatbed Towing', 'Jump Starts'].map(svc => (
            <li key={svc}><Link to="/services" className="hover:text-white transition-colors">{svc}</Link></li>
          ))}
        </ul>
      </div>
    </>
  );
}

function FooterServiceAreas() {
  return (
    <div>
      <h3 className="text-white font-semibold mb-4">Service Areas</h3>
      <ul className="space-y-2 text-gray-400 text-sm">
        {SEO_BASE.serviceAreas.map(area => (
          <li key={area}><Link to={`/service-area/${area.toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-white transition-colors">{area}</Link></li>
        ))}
      </ul>
      <a href={GBP_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-find-on-google">
        <MapPin className="w-3.5 h-3.5 text-red-600" />Find Us on Google Maps<ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function Footer() {
  return (
    <footer className="bg-zinc-950 border-t border-zinc-800" data-testid="site-footer" itemScope itemType="https://schema.org/TowingCompany">
      <meta itemProp="name" content="A2B Towing & Roadside Assistance" />
      <meta itemProp="telephone" content="+1-916-234-3923" />
      <meta itemProp="email" content="A2broadside916@gmail.com" />
      <meta itemProp="url" content="https://a2btowing.org" />
      <meta itemProp="priceRange" content="$$" />
      <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
        <meta itemProp="addressLocality" content="Orangevale" />
        <meta itemProp="addressRegion" content="CA" />
        <meta itemProp="postalCode" content="95662" />
        <meta itemProp="addressCountry" content="US" />
      </div>
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <FooterContactInfo />
          <FooterLinks />
          <FooterServiceAreas />
        </div>
      </div>
      <div className="border-t border-zinc-800">
        <div className="container mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm" data-testid="footer-copyright">&copy; {new Date().getFullYear()} A2B Towing & Roadside Assistance. All rights reserved.</p>
          <p className="text-gray-500 text-sm">Serving Orangevale & Sacramento County | Licensed & Insured</p>
        </div>
      </div>
    </footer>
  );
}

function Layout({ children }) {
  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export default Layout;
export { Header, Footer };
